import { useMutation, useQuery } from '@apollo/client/react';
import { PlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  CREATE_COMPANY_SERVICE,
  type CreateCompanyServiceResponse,
  DELETE_COMPANY_SERVICE,
  type DeleteCompanyServiceResponse,
  GET_COMPANY_OVERVIEW,
  type GetCompanyOverviewResponse,
  UPDATE_COMPANY_SERVICE,
  type UpdateCompanyServiceResponse
} from './graphql';
import { TableCard, type TableRow } from './section-ui';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import NumberField from '@/components/NumberField';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';

export function ServicesTab({
  dealOpportunityId
}: {
  dealOpportunityId: string;
}) {
  const { data: companyOverviewData, refetch: refetchCompanyOverview } =
    useQuery<GetCompanyOverviewResponse>(GET_COMPANY_OVERVIEW, {
      skip: !dealOpportunityId,
      variables: { deal_opportunity_id: dealOpportunityId }
    });

  const [createCompanyService, { loading: isCreatingService }] =
    useMutation<CreateCompanyServiceResponse>(CREATE_COMPANY_SERVICE);
  const [updateCompanyService, { loading: isUpdatingService }] =
    useMutation<UpdateCompanyServiceResponse>(UPDATE_COMPANY_SERVICE);
  const [deleteCompanyService, { loading: isDeletingService }] =
    useMutation<DeleteCompanyServiceResponse>(DELETE_COMPANY_SERVICE);

  const companyOverview = companyOverviewData?.companyOverviewByDeal;
  const servicesRows: TableRow[] = (
    companyOverview?.companyServicesIds ?? []
  ).map(service => ({
    description: service.description,
    id: service.id,
    name: service.name,
    revenueShare: service.percentOfRevenue
  }));

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<null | TableRow>(null);
  const [deletingServiceId, setDeletingServiceId] = useState<null | string>(
    null
  );
  const [draft, setDraft] = useState<{
    description: string;
    name: string;
    revenueShare: string;
  }>({
    description: '',
    name: '',
    revenueShare: ''
  });

  useEffect(() => {
    if (isDialogOpen && !editingService) {
      setDraft({ description: '', name: '', revenueShare: '' });
    } else if (editingService) {
      setDraft({
        description: editingService.description ?? '',
        name: editingService.name ?? '',
        revenueShare:
          editingService.revenueShare != null
            ? String(editingService.revenueShare * 100)
            : ''
      });
    }
  }, [isDialogOpen, editingService]);

  const handleSaveService = async () => {
    if (!companyOverview) return;
    try {
      if (editingService) {
        const { data } = await updateCompanyService({
          variables: {
            id: editingService.id,
            values: {
              description: draft.description || null,
              name: draft.name || null,
              percentOfRevenue: draft.revenueShare
                ? parseFloat(draft.revenueShare) / 100
                : null
            }
          }
        });
        if (data?.updateCompanyService.ok) {
          toast.success('Service updated successfully');
          setIsDialogOpen(false);
          setEditingService(null);
          void refetchCompanyOverview();
        } else {
          toast.error(
            data?.updateCompanyService.errors[0] ?? 'Failed to update service'
          );
        }
      } else {
        const { data } = await createCompanyService({
          variables: {
            companyOverviewId: companyOverview.id,
            values: {
              description: draft.description || null,
              name: draft.name || null,
              percentOfRevenue: draft.revenueShare
                ? parseFloat(draft.revenueShare) / 100
                : null
            }
          }
        });
        if (data?.createCompanyService.ok) {
          toast.success('Service created successfully');
          setIsDialogOpen(false);
          void refetchCompanyOverview();
        } else {
          toast.error(
            data?.createCompanyService.errors[0] ?? 'Failed to create service'
          );
        }
      }
    } catch {
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    try {
      const { data } = await deleteCompanyService({
        variables: { id: serviceId }
      });
      if (data?.deleteCompanyService.ok) {
        toast.success('Service deleted successfully');
        setDeletingServiceId(null);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.deleteCompanyService.errors[0] ?? 'Failed to delete service'
        );
      }
    } catch {
      toast.error('Failed to delete service');
    }
  };

  return (
    <TableCard
      actions={
        <>
          <Dialog
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) setEditingService(null);
            }}
            open={isDialogOpen}
          >
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle>
                  {editingService ? 'Edit Service' : 'New Service'}
                  <Badge variant="yellow">Unsaved</Badge>
                </DialogTitle>
              </DialogHeader>

              <DialogBody>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="services-name-field">Name</FieldLabel>
                    <Input
                      id="services-name-field"
                      onChange={event => {
                        setDraft(current => ({
                          ...current,
                          name: event.target.value
                        }));
                      }}
                      value={draft.name}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="services-revenue-field">
                      % of LTM Revenue
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="services-revenue-field"
                        max={100}
                        min={0}
                        onChange={event => {
                          setDraft(current => ({
                            ...current,
                            revenueShare: event.target.value
                          }));
                        }}
                        value={draft.revenueShare}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="services-description-field">
                      Description
                    </FieldLabel>
                    <Textarea
                      id="services-description-field"
                      onChange={event => {
                        setDraft(current => ({
                          ...current,
                          description: event.target.value
                        }));
                      }}
                      value={draft.description}
                    />
                  </Field>
                </FieldGroup>
              </DialogBody>

              <DialogFooter>
                <Button
                  disabled={isCreatingService || isUpdatingService}
                  onClick={() => void handleSaveService()}
                  type="button"
                >
                  {(isCreatingService || isUpdatingService) && <Spinner />}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <AlertDialog
            onOpenChange={open => {
              if (!open) setDeletingServiceId(null);
            }}
            open={!!deletingServiceId}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Service</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this service? This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={isDeletingService}
                  onClick={() => {
                    if (deletingServiceId) {
                      void handleDeleteService(deletingServiceId);
                    }
                  }}
                >
                  {isDeletingService && <Spinner />}
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
      emptyAction={
        <Button
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          <PlusIcon />
          Add Service
        </Button>
      }
      onDelete={id => {
        setDeletingServiceId(id);
      }}
      onEdit={row => {
        setEditingService(row);
        setIsDialogOpen(true);
      }}
      rows={servicesRows}
      title="Services"
    />
  );
}
